/**
 * Загрузка парка техники списком — данные и правила экрана (AK-804).
 *
 * Макет утверждён founder 06.09:
 * `docs/superpowers/prototypes/machinery-forms/04-import-from-file.html`.
 *
 * Здесь ЧИСТЫЕ функции и запросы; разметка мастера — в
 * `pages/structure/farm-card/machinery/ImportMachineryDialog.tsx`. Правила
 * («сколько машин заведём», «что написать на кнопке», «что сказать в итоге»)
 * живут тут, потому что именно их проверяют тесты — не разметку.
 */
import { useMutation } from '@tanstack/react-query';
import { API_URL, ApiError, api } from '@/lib/api';

export type ImportRowStatus = 'matched' | 'needs_model' | 'duplicate' | 'error';

export interface ImportColumn {
  source: string;
  field: string | null;
  title: string | null;
  recognized: boolean;
}

export interface ImportRow {
  row_number: number;
  status: ImportRowStatus;
  name: string;
  inventory_number: string | null;
  reg_number: string | null;
  brigade_text: string | null;
  brigade_id: string | null;
  year_made: number | null;
  power_hp: number | null;
  working_width_m: number | null;
  ref_id: string | null;
  ref_name: string | null;
  type_name: string | null;
  machinery_group: string | null;
  matched_via: string | null;
  duplicate_of: string | null;
  duplicate_by: string | null;
  duplicate_in_file: boolean;
  no_ids: boolean;
  warnings: string[];
  errors: string[];
}

export interface ImportPreview {
  file_name: string;
  farm_id: string;
  columns: ImportColumn[];
  rows: ImportRow[];
  counts: Record<string, number>;
}

export interface ApplyResult {
  created: number;
  skipped_existing: number;
  queued: number;
  skipped_no_ids: number;
  errors: number;
}

/** Потолок файла — тот же, что у сервера (`services/budget_catalog_import`). */
export const IMPORT_MAX_MB = 10;
export const IMPORT_MAX_ROWS = 5000;

/** Подписи чипов-фильтров шага «Проверка» — языком человека, не статусами. */
export const STATUS_TITLES: Record<ImportRowStatus, string> = {
  matched: 'Заведём',
  needs_model: 'Выберите модель',
  duplicate: 'Уже есть',
  error: 'Ошибка',
};

/**
 * Сколько машин реально поедет в парк.
 *
 * Строки без инвентарного и гос. номера считаются ТОЛЬКО когда человек снял
 * предупреждение флажком (решение founder 06.09, п.1): повторная загрузка того
 * же файла их не отличит и задвоит парк, поэтому по умолчанию они не едут.
 */
export function plannedCount(rows: ImportRow[], allowWithoutIds: boolean): number {
  return rows.filter((r) => r.status === 'matched' && (allowWithoutIds || !r.no_ids)).length;
}

/** Сколько строк ждут флажка «загрузить всё равно». */
export function noIdsCount(rows: ImportRow[]): number {
  return rows.filter((r) => r.status === 'matched' && r.no_ids).length;
}

export function machineryPlural(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} машину`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} машины`;
  return `${count} машин`;
}

/** Подпись главной кнопки шага «Проверка» — цифра в ней обязана быть честной. */
export function applyButtonLabel(count: number): string {
  return count === 0 ? 'Заводить нечего' : `Применить — заведём ${machineryPlural(count)}`;
}

/** Итог шага 3. Говорим и про то, что НЕ поехало: молчание читается как потеря. */
export function doneSummary(result: ApplyResult, farmName: string): string[] {
  const lines = [
    result.created === 0
      ? 'Новых машин не завели — всё из файла уже было в парке.'
      : `Готово. Завели ${machineryPlural(result.created)} в хозяйство «${farmName}».`,
  ];
  const skipped: string[] = [];
  if (result.skipped_existing) skipped.push(`${result.skipped_existing} уже было в парке`);
  if (result.queued) skipped.push(`${result.queued} без модели ушли в очередь разбора справочника`);
  if (result.skipped_no_ids) skipped.push(`${result.skipped_no_ids} без инвентарного и гос. номера не грузили`);
  if (result.errors) skipped.push(`${result.errors} с ошибкой в файле`);
  if (skipped.length) lines.push(`Не завели: ${skipped.join(', ')}.`);
  return lines;
}

/**
 * Выбор модели применяется ко ВСЕМ строкам файла с таким же наименованием:
 * двенадцать одинаковых борон человек подтверждает один раз (макет, шаг 2).
 */
export function withChosenModel(
  rows: ImportRow[],
  rawName: string,
  model: { id: string; name: string; type_name: string | null } | null,
): ImportRow[] {
  const target = rawName.trim().toLowerCase();
  return rows.map((row) => {
    if (row.name.trim().toLowerCase() !== target) return row;
    if (model === null) {
      return { ...row, status: 'needs_model', ref_id: null, ref_name: null, matched_via: null };
    }
    return {
      ...row,
      // Строка со снятым «выберите модель» становится обычной «заведём» — и
      // предупреждение о номерах к ней применяется на общих основаниях.
      status: 'matched',
      ref_id: model.id,
      ref_name: model.name,
      type_name: model.type_name,
      matched_via: 'chosen',
      no_ids: !row.inventory_number && !row.reg_number,
    };
  });
}

/** Текст отказа — дословно с сервера, если он осмысленный. */
export function importErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = err.rawDetail as { message?: string } | undefined;
    if (detail?.message) return detail.message;
    if (err.status === 403) return 'Нет прав заводить технику в этом хозяйстве — обратитесь к администратору.';
    if (err.status === 413) return `Файл слишком большой — до ${IMPORT_MAX_MB} МБ.`;
  }
  return 'Не получилось прочитать файл — проверьте формат (.xlsx или .csv) и попробуйте ещё раз.';
}

/**
 * Образец качаем blob'ом, а не `<a download>`: сессия в cookie, но разбор
 * ошибки (403 без права) нужен в JS — иначе браузер молча откроет страницу
 * входа вместо файла.
 */
export async function downloadParkTemplate(): Promise<void> {
  const resp = await fetch(`${API_URL}/api/v1/company/machinery/import/template`, {
    credentials: 'include',
  });
  if (!resp.ok) {
    throw new ApiError(
      resp.status,
      resp.status === 403
        ? 'Образец доступен тем, кому открыто ведение справочников компании.'
        : 'Не удалось скачать образец — попробуйте ещё раз.',
    );
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'образец-парк-техники.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari/Firefox обрывают скачивание, если ссылку отозвать в том же тике.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function usePreviewParkImport(farmId: string) {
  return useMutation<ImportPreview, Error, File>({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('file', file);
      form.append('farm_id', farmId);
      return api.uploadFile<ImportPreview>('/api/v1/company/machinery/import/preview', form);
    },
  });
}

export function useApplyParkImport(farmId: string) {
  return useMutation<ApplyResult, Error, { rows: ImportRow[]; fileName: string; allowWithoutIds: boolean }>({
    mutationFn: ({ rows, fileName, allowWithoutIds }) =>
      api.post<ApplyResult>('/api/v1/company/machinery/import/apply', {
        farm_id: farmId,
        file_name: fileName,
        allow_without_ids: allowWithoutIds,
        rows: rows.map((row) => ({
          row_number: row.row_number,
          name: row.name,
          inventory_number: row.inventory_number,
          reg_number: row.reg_number,
          brigade_text: row.brigade_text,
          year_made: row.year_made,
          power_hp: row.power_hp,
          working_width_m: row.working_width_m,
          ref_id: row.ref_id,
        })),
      }),
  });
}
